import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Button } from '../components/ui/Button';
import { ErrorBanner } from '../components/ui/ErrorBanner';
import { PageHeader } from '../components/ui/PageHeader';
import {
  AUFTRAG_STATUS_BADGE,
  AUFTRAG_STATUS_LABEL,
  AUFTRAG_TYP_LABEL,
  auftraegeApi,
  type Auftrag,
  type AuftragStatus,
  type Bericht,
  type CreateBerichtInput,
  type EntscheideDurchBueroInput,
  type KlassifiziereDurchMaInput,
} from '../lib/auftraege-api';
import { requireAccess } from '../lib/guards';
import { useSession } from '../lib/session';
import { formatGermanDateFromTimestamp, formatGermanDateTime } from '../lib/date-format';
import { MaKlassifizierungBlock } from '../components/auftraege/MaKlassifizierungBlock';
import { BueroEntscheidungBlock } from '../components/auftraege/BueroEntscheidungBlock';
import { StatusActionBar } from '../components/auftraege/StatusActionBar';
import { FotoUploader } from '../components/ui/FotoUploader';

export const Route = createFileRoute('/auftraege/$id')({
  beforeLoad: () => requireAccess('/api/v1/auftraege'),
  component: AuftragDetailPage,
});

type Tab = 'stamm' | 'berichte' | 'reklamation' | 'bautagebuch';

const TAB_LABEL: Record<Tab, string> = {
  stamm: 'Stamm',
  berichte: 'Berichte',
  reklamation: 'Reklamation',
  bautagebuch: 'Bautagebuch',
};

function AuftragDetailPage(): JSX.Element {
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const session = useSession();
  const [tab, setTab] = useState<Tab>('stamm');

  const auftragQ = useQuery({
    queryKey: ['auftraege', 'detail', id],
    queryFn: () => auftraegeApi.getById(id),
  });

  function invalidateAuftrag() {
    void qc.invalidateQueries({ queryKey: ['auftraege'] });
    void qc.invalidateQueries({ queryKey: ['reklamationen'] });
  }

  const confirm = useMutation({
    mutationFn: () => auftraegeApi.confirm(id),
    onSuccess: invalidateAuftrag,
  });

  const setStatus = useMutation({
    mutationFn: (status: AuftragStatus) => auftraegeApi.setStatus(id, status),
    onSuccess: invalidateAuftrag,
  });

  const klassifizieren = useMutation({
    mutationFn: (input: KlassifiziereDurchMaInput) => auftraegeApi.klassifizieren(id, input),
    onSuccess: invalidateAuftrag,
  });

  const entscheiden = useMutation({
    mutationFn: (input: EntscheideDurchBueroInput) => auftraegeApi.entscheiden(id, input),
    onSuccess: invalidateAuftrag,
  });

  const auftrag = auftragQ.data?.item;
  const canEntscheiden = session.data?.role === 'admin' || session.data?.role === 'buero';

  return (
    <div className="space-y-6">
      <PageHeader
        title={auftrag?.titel ?? 'Auftrag'}
        description={
          auftrag
            ? `${AUFTRAG_TYP_LABEL[auftrag.typ]} · erstellt ${formatGermanDateFromTimestamp(
                auftrag.createdAt,
              )}`
            : '…'
        }
        actions={
          <Button variant="ghost" onClick={() => void navigate({ to: '/auftraege' })}>
            ← Zurück
          </Button>
        }
      />

      {auftragQ.error ? <ErrorBanner error={auftragQ.error as Error} /> : null}
      {confirm.error ? <ErrorBanner error={confirm.error as Error} /> : null}
      {setStatus.error ? <ErrorBanner error={setStatus.error as Error} /> : null}
      {klassifizieren.error ? <ErrorBanner error={klassifizieren.error as Error} /> : null}
      {entscheiden.error ? <ErrorBanner error={entscheiden.error as Error} /> : null}

      {!auftrag ? (
        <div className="text-sm text-slate-500">Lädt…</div>
      ) : (
        <>
          <StatusActionBar
            auftrag={auftrag}
            onConfirm={() => confirm.mutate()}
            onSetStatus={(s) => setStatus.mutate(s)}
            confirmPending={confirm.isPending}
            setStatusPending={setStatus.isPending}
          />
          <div className="flex flex-wrap items-center gap-3">
            <span
              className={`rounded-md border px-2 py-1 text-xs font-medium ${AUFTRAG_STATUS_BADGE[auftrag.status]}`}
            >
              {AUFTRAG_STATUS_LABEL[auftrag.status]}
            </span>
            {auftrag.status !== 'entwurf' ? (
              <a
                className="inline-flex items-center gap-2 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50"
                href={auftraegeApi.abPdfUrl(auftrag.id)}
                target="_blank"
                rel="noopener noreferrer"
              >
                AB-PDF öffnen
              </a>
            ) : null}
          </div>

          <MaKlassifizierungBlock
            auftrag={auftrag}
            onKlassifizieren={(input) => klassifizieren.mutate(input)}
            busy={klassifizieren.isPending}
          />
          <BueroEntscheidungBlock
            auftrag={auftrag}
            canEntscheiden={canEntscheiden}
            onEntscheiden={(input) => entscheiden.mutate(input)}
            busy={entscheiden.isPending}
          />

          <TabBar current={tab} onChange={setTab} />

          {tab === 'stamm' ? <StammTab auftrag={auftrag} /> : null}
          {tab === 'berichte' ? <BerichteTab auftragId={auftrag.id} /> : null}
          {tab === 'reklamation' ? <PendingTab label="Reklamation" /> : null}
          {tab === 'bautagebuch' ? <PendingTab label="Bautagebuch" /> : null}
        </>
      )}
    </div>
  );
}

function TabBar({ current, onChange }: { current: Tab; onChange: (t: Tab) => void }): JSX.Element {
  const tabs: Tab[] = ['stamm', 'berichte', 'reklamation', 'bautagebuch'];
  return (
    <div className="flex flex-wrap gap-2 border-b border-slate-200">
      {tabs.map((t) => (
        <button
          key={t}
          type="button"
          onClick={() => onChange(t)}
          className={`px-3 py-2 text-sm font-medium ${
            current === t
              ? 'border-b-2 border-brand-600 text-brand-700'
              : 'border-b-2 border-transparent text-slate-600 hover:text-slate-900'
          }`}
        >
          {TAB_LABEL[t]}
        </button>
      ))}
    </div>
  );
}

function StammTab({ auftrag }: { auftrag: Auftrag }): JSX.Element {
  return (
    <dl className="grid gap-4 rounded-lg border border-slate-200 bg-white p-4 sm:grid-cols-2">
      <Field label="Typ">{AUFTRAG_TYP_LABEL[auftrag.typ]}</Field>
      <Field label="Quelle">{auftrag.quelle}</Field>
      <Field label="Kunde">
        <Link
          to="/kunden/$id"
          params={{ id: auftrag.kundeId }}
          className="text-brand-700 hover:underline"
        >
          Kunde öffnen
        </Link>
      </Field>
      <Field label="Objekt">
        {auftrag.objektId ? (
          <Link
            to="/objekte/$id"
            params={{ id: auftrag.objektId }}
            className="text-brand-700 hover:underline"
          >
            Objekt öffnen
          </Link>
        ) : (
          <span className="text-slate-400">—</span>
        )}
      </Field>
      <Field label="Geplant für">
        {auftrag.scheduledAt ? (
          formatGermanDateTime(auftrag.scheduledAt)
        ) : (
          <span className="text-slate-400">—</span>
        )}
      </Field>
      <Field label="Beschreibung" wide>
        {auftrag.beschreibung ?? <span className="text-slate-400">—</span>}
      </Field>
    </dl>
  );
}

function Field({
  label,
  children,
  wide,
}: {
  label: string;
  children: React.ReactNode;
  wide?: boolean;
}): JSX.Element {
  return (
    <div className={wide ? 'sm:col-span-2' : ''}>
      <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">{label}</dt>
      <dd className="mt-1 text-sm text-slate-900 whitespace-pre-wrap">{children}</dd>
    </div>
  );
}

function PendingTab({ label }: { label: string }): JSX.Element {
  return (
    <div className="rounded-lg border border-dashed border-slate-300 p-8 text-center text-sm text-slate-500">
      {label}-UI folgt in Folge-Slice (Stufe I).
    </div>
  );
}

function BerichteTab({ auftragId }: { auftragId: string }): JSX.Element {
  const qc = useQueryClient();
  const [text, setText] = useState('');

  const berichteQ = useQuery({
    queryKey: ['auftraege', 'berichte', auftragId],
    queryFn: () => auftraegeApi.listBerichte(auftragId),
  });

  const create = useMutation({
    mutationFn: (input: CreateBerichtInput) => auftraegeApi.createBericht(auftragId, input),
    onSuccess: () => {
      setText('');
      void qc.invalidateQueries({ queryKey: ['auftraege', 'berichte', auftragId] });
    },
  });

  const items = berichteQ.data?.items ?? [];
  const canSubmit = text.trim().length >= 3 && !create.isPending;

  return (
    <div className="space-y-4">
      {berichteQ.error ? <ErrorBanner error={berichteQ.error as Error} /> : null}
      {create.error ? <ErrorBanner error={create.error as Error} /> : null}

      <form
        className="space-y-3 rounded-lg border border-slate-200 bg-white p-4"
        onSubmit={(e) => {
          e.preventDefault();
          if (canSubmit) create.mutate({ text: text.trim() });
        }}
      >
        <label className="block">
          <span className="mb-1 block text-sm font-medium text-slate-700">Neuer Bericht</span>
          <textarea
            className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
            rows={3}
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Was wurde gemacht? Was war auffällig?"
          />
        </label>
        <Button type="submit" disabled={!canSubmit}>
          {create.isPending ? 'Speichere…' : 'Bericht anlegen'}
        </Button>
      </form>

      {berichteQ.isLoading ? (
        <div className="text-sm text-slate-500">Lädt…</div>
      ) : items.length === 0 ? (
        <div className="rounded-lg border border-dashed border-slate-300 p-6 text-center text-sm text-slate-500">
          Noch keine Berichte zu diesem Auftrag.
        </div>
      ) : (
        <ul className="space-y-3">
          {items.map((b) => (
            <BerichtCard key={b.id} auftragId={auftragId} bericht={b} />
          ))}
        </ul>
      )}
    </div>
  );
}

function BerichtCard({ auftragId, bericht }: { auftragId: string; bericht: Bericht }): JSX.Element {
  const qc = useQueryClient();

  async function handleUpload(file: File): Promise<void> {
    await auftraegeApi.uploadBerichtFoto(auftragId, bericht.id, file);
    await qc.invalidateQueries({ queryKey: ['auftraege', 'berichte', auftragId] });
  }

  return (
    <li className="rounded-lg border border-slate-200 bg-white p-4">
      <div className="text-xs text-slate-500">
        {formatGermanDateTime(bericht.erstelltAm)}
        {bericht.fotos.length > 0 ? ` · ${bericht.fotos.length} Foto(s)` : null}
      </div>
      <div className="mt-2 whitespace-pre-wrap text-sm text-slate-900">{bericht.text}</div>

      {bericht.fotos.length > 0 ? (
        <div className="mt-3 grid grid-cols-3 gap-2 sm:grid-cols-4">
          {bericht.fotos.map((f) => (
            <a
              key={f.id}
              href={auftraegeApi.berichtFotoUrl(auftragId, bericht.id, f.id)}
              target="_blank"
              rel="noopener noreferrer"
              className="aspect-square overflow-hidden rounded border border-slate-200 bg-slate-50"
              title={f.originalFilename}
            >
              <img
                src={auftraegeApi.berichtFotoUrl(auftragId, bericht.id, f.id)}
                alt={f.originalFilename}
                className="h-full w-full object-cover"
                loading="lazy"
              />
            </a>
          ))}
        </div>
      ) : null}

      <div className="mt-3">
        <FotoUploader onUpload={handleUpload} buttonLabel="Fotos hinzufügen" />
      </div>
    </li>
  );
}
